"use client";

import { AddCreditsDialog, SpendLimitDialog } from "@/app/(app)/manage/billing/billing.overview";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useOptionalWelcomeCreditReveal } from "@/components/welcome-credit-reveal";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useCreditTopUp } from "@/lib/billing/useCreditTopUp";
import { orgRoleCanManageBilling } from "@/lib/org/org-constants";
import { formatCostUsd, useUsageSummary } from "@/lib/usage/usage.hooks";

const formatUsdFromMicro = (micro: string | number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(micro ?? 0) / 1_000_000);

/**
 * Two-card summary row: organization credits + spend this month.
 */
export const DashboardUsageStats = () => {
  const { orgRole } = useAuth();
  const canManageBilling = orgRoleCanManageBilling(orgRole);
  const { data: billingData, isPending: billingPending, isError: billingError } = useBillingOverview();
  const { data: usageData, isLoading: usageLoading } = useUsageSummary({ preset: "current_period" });
  const topUp = useCreditTopUp();
  const welcomeReveal = useOptionalWelcomeCreditReveal();

  const overview = billingData?.billing_overview;
  const creditBalance = welcomeReveal
    ? welcomeReveal.displayBalanceMicro(overview?.credit_balance_micro)
    : overview?.credit_balance_micro;
  const spendCents = usageData?.usage_summary?.estimated_cost_cents ?? 0;
  const spendLimitMicro = overview?.monthly_spend_limit_micro;
  const hasSpendLimit = spendLimitMicro != null;

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card className="gap-0 py-5">
        <CardContent className="flex flex-col items-start gap-6 px-5">
          <div className="min-w-0 space-y-3">
            <p className="font-mono text-xs tracking-wide text-muted-foreground uppercase">Organization credits</p>
            {billingPending ? (
              <Skeleton className="h-12 w-40" />
            ) : billingError || overview == null ? (
              <p className="font-sans text-5xl font-semibold tracking-tight text-muted-foreground">—</p>
            ) : (
              <p className="font-sans text-5xl font-semibold tracking-tight">{formatUsdFromMicro(creditBalance)}</p>
            )}
          </div>
          {canManageBilling ? (
            overview ? (
              <AddCreditsDialog
                topUp={topUp}
                paymentMethods={overview.payment_methods}
                invoices={overview.invoices}
                monthlySpendLimitMicro={overview.monthly_spend_limit_micro}
                currentSettings={overview.auto_recharge}
                trigger={
                  <Button variant="outline" size="sm">
                    Add credits
                  </Button>
                }
              />
            ) : (
              <Button variant="outline" size="sm" disabled>
                Add credits
              </Button>
            )
          ) : null}
        </CardContent>
      </Card>

      <Card className="gap-0 py-5">
        <CardContent className="flex flex-col items-start gap-6 px-5">
          <div className="min-w-0 space-y-3">
            <p className="font-mono text-xs tracking-wide text-muted-foreground uppercase">Spend this month</p>
            {usageLoading || billingPending ? (
              <Skeleton className="h-12 w-40" />
            ) : (
              <p className="font-sans text-5xl font-semibold tracking-tight">{formatCostUsd(spendCents)}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canManageBilling ? (
              <SpendLimitDialog
                currentLimitMicro={spendLimitMicro}
                isLoading={billingPending}
                trigger={
                  <Button variant="outline" size="sm" disabled={billingPending}>
                    {hasSpendLimit ? "Change limit" : "Set limit"}
                  </Button>
                }
              />
            ) : null}
            {!billingPending && hasSpendLimit ? (
              <p className="text-sm text-muted-foreground">Spend limit {formatUsdFromMicro(spendLimitMicro)}</p>
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
